/**
 * Lista de instalações com inflamáveis (Laudo NR 20, #366): filtros em linha, classe com o motivo,
 * inflamáveis armazenados, prontuário "x de N em dia", próxima revisão e situação — tudo calculado
 * pelo servidor; a lista só mostra.
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Alert, Box, Button, Tooltip, Typography } from '@mui/material';
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
  Visibility as ViewIcon,
} from '@mui/icons-material';
import {
  ControleIconButton,
  DataTable,
  DeleteWithDependenciesDialog,
  InlineFilterBar,
  InlineSelect,
  PageShell,
  RouteActionButton,
  type DataTableColumn,
} from '../../components';
import { pode } from '../../authorization/pode';
import { useListaPermitida } from '../../authorization/useListaPermitida';
import { useAcao, useControle } from '../../authorization/useVeredito';
import { ValidityChip } from '../../components/common/ValidityChip';
import { useDeleteWithDependencies } from '../../hooks/useDeleteWithDependencies';
import { useFilterState } from '../../hooks/useFilterState';
import { departmentService } from '../../services/departmentService';
import {
  flammableInstallationService,
  ROTULO_DA_ATIVIDADE,
  ROTULO_DA_CLASSE,
  ROTULO_DA_SITUACAO_DA_INSTALACAO,
  type FlammableClass,
  type FlammableInstallation,
  type FlammableInstallationFilters,
  type FlammableSituation,
} from '../../services/flammableInstallationService';
import type { DependencyCheck } from '../../types/dependencyCheck';
import { dependencyItems } from '../../utils/dependencyItems';
import { ClassChip, SituationChip } from './components/FlammableChips';
import { diasAteVencer, formatarData, hojeComoTexto } from './components/flammableDates';
import { mensagemDoServidor } from './components/mensagensDeErro';
import { capacidadesEmTexto, motivoDaClasse, produtosEmTexto } from './components/flammableTexts';

/** Os setores do filtro; a lista só é pedida a quem pode ler setores (`useListaPermitida`). */
const loadDepartmentOptions = async (): Promise<readonly { value: string; label: string }[]> => {
  const response = await departmentService.getDepartments({ page: 1, limit: 1000 });
  return response.data.map((department) => ({ value: String(department.id), label: department.name }));
};

const CLASS_OPTIONS = (Object.keys(ROTULO_DA_CLASSE) as FlammableClass[]).map((classe) => ({
  value: classe,
  label: ROTULO_DA_CLASSE[classe],
}));

const SITUATION_OPTIONS = (Object.keys(ROTULO_DA_SITUACAO_DA_INSTALACAO) as FlammableSituation[]).map(
  (situation) => ({ value: situation, label: ROTULO_DA_SITUACAO_DA_INSTALACAO[situation] })
);

const columnStackSx = { display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: 0.5 } as const;

interface InstallationFiltersData {
  search: string;
  departmentId: string;
  class: string;
  situation: string;
}

const DEFAULT_FILTERS: InstallationFiltersData = { search: '', departmentId: '', class: '', situation: '' };

/** O prontuário em uma frase curta e, embaixo, o que falta. */
const ProntuarioCell: React.FC<{ installation: FlammableInstallation }> = ({ installation }) => {
  const { summary } = installation;
  const { classification } = summary;
  if (classification.class === 'SEM_DADOS') return <>—</>;
  if (summary.requiredCount === 0) return <>Não exigido</>;

  const pendencias: string[] = [];
  if (summary.missingCount > 0) pendencias.push(`${summary.missingCount} ausente${summary.missingCount > 1 ? 's' : ''}`);
  if (summary.overdueCount > 0) pendencias.push(`${summary.overdueCount} vencido${summary.overdueCount > 1 ? 's' : ''}`);
  if (summary.upToDateCount === 0 && summary.missingCount === summary.requiredCount) {
    pendencias.splice(0, pendencias.length, 'nenhum registro');
  }

  return (
    <Box sx={columnStackSx}>
      <span>{`${summary.upToDateCount} de ${summary.requiredCount} em dia`}</span>
      {pendencias.length > 0 && (
        <Typography variant="body2" color="text.secondary" sx={{ fontSize: '1rem' }}>
          {pendencias.join(' · ')}
        </Typography>
      )}
    </Box>
  );
};

const ProximaRevisaoCell: React.FC<{ nextDueDate: string | null }> = ({ nextDueDate }) => {
  if (!nextDueDate) return <>—</>;
  const dias = diasAteVencer(nextDueDate, hojeComoTexto());
  if (dias < 0) {
    return <ValidityChip situation="VENCIDO" days={dias} label={`Vencida · ${formatarData(nextDueDate)}`} />;
  }
  return <>{formatarData(nextDueDate)}</>;
};

export const FlammableInstallationListPage: React.FC = () => {
  const navigate = useNavigate();
  const ver = useControle('/flammable-installations/:id', pode('flammable_installation.read'), 'Ver');
  const editar = useControle('/flammable-installations/:id', pode('flammable_installation.update'), 'Editar');
  const excluir = useAcao('flammable_installation.delete', 'Excluir');
  const criar = useAcao('flammable_installation.create', 'Cadastrar a primeira instalação');

  const [installations, setInstallations] = useState<FlammableInstallation[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [total, setTotal] = useState(0);
  const [apiFilters, setApiFilters] = useState<FlammableInstallationFilters>({});
  const departmentList = useListaPermitida('department.read', 'Setores', loadDepartmentOptions);
  const departments = useMemo(() => [...departmentList.itens], [departmentList.itens]);

  const loadInstallations = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await flammableInstallationService.getInstallations({
        page: page + 1,
        limit: rowsPerPage,
        ...apiFilters,
      });
      setInstallations(response.installations);
      setTotal(response.total);
    } catch (err: unknown) {
      setError(mensagemDoServidor(err, 'Erro ao carregar as instalações com inflamáveis'));
    } finally {
      setLoading(false);
    }
  }, [page, rowsPerPage, apiFilters]);

  useEffect(() => {
    loadInstallations();
  }, [loadInstallations]);

  const handleFilter = useCallback((data: InstallationFiltersData) => {
    const filters: FlammableInstallationFilters = {};
    if (data.search) filters.search = data.search;
    if (data.departmentId) filters.departmentId = Number(data.departmentId);
    if (data.class) filters.class = data.class as FlammableClass;
    if (data.situation) filters.situation = data.situation as FlammableSituation;
    setApiFilters(filters);
    setPage(0);
  }, []);

  const { filters, setFilter, clearAll, activeChips } = useFilterState(DEFAULT_FILTERS, {
    onFilter: handleFilter,
    applyOnMount: true,
    live: true,
    labels: { departmentId: 'Setor', class: 'Classe', situation: 'Situação' },
    formatValue: {
      departmentId: (value: string) => departments.find((option) => option.value === value)?.label ?? value,
      class: (value: string) => CLASS_OPTIONS.find((option) => option.value === value)?.label ?? value,
      situation: (value: string) => SITUATION_OPTIONS.find((option) => option.value === value)?.label ?? value,
    },
  });

  const filtersActive = Object.keys(apiFilters).length > 0;

  const { deleteTarget, deleteDependencies, handleDeleteClick, closeDeleteDialog, handleDeleteConfirm } =
    useDeleteWithDependencies<FlammableInstallation, DependencyCheck>({
      checkDependencies: (id) => flammableInstallationService.checkDependencies(id),
      deleteEntity: (id) => flammableInstallationService.deleteInstallation(id),
      checkErrorMessage: 'Erro ao verificar o que está dentro da instalação',
      deleteErrorMessage: 'Não foi possível excluir a instalação.',
      onError: setError,
      onDeleted: loadInstallations,
    });

  const departmentName = useCallback(
    (departmentId: number | string | null): string =>
      departmentId === null
        ? 'Sem setor'
        : (departments.find((option) => option.value === String(departmentId))?.label ?? '—'),
    [departments]
  );

  const openInstallation = useCallback(
    (installation: FlammableInstallation) => navigate(`/flammable-installations/${installation.id}`),
    [navigate]
  );

  // Ícones de 44px: o `actions` do DataTable é comum e fixa 30px, então a coluna de ações é daqui.
  const columns = useMemo<DataTableColumn<FlammableInstallation>[]>(
    () => [
      {
        id: 'name',
        label: 'Instalação',
        primary: true,
        render: (installation) => (
          <Box>
            <Typography variant="body1" fontWeight="medium">
              {installation.name}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {[installation.tag, departmentName(installation.departmentId)].filter(Boolean).join(' · ')}
            </Typography>
          </Box>
        ),
      },
      {
        id: 'activity',
        label: 'Atividade',
        render: (installation) =>
          installation.activityType === 'OUTRA' && installation.activityDescription
            ? installation.activityDescription
            : ROTULO_DA_ATIVIDADE[installation.activityType],
      },
      {
        id: 'class',
        label: 'Classe',
        render: ({ summary }) =>
          summary.situation === 'DESATIVADA' ? (
            '—'
          ) : (
            <Box sx={columnStackSx}>
              <ClassChip classe={summary.classification.class} />
              <Typography variant="body2" color="text.secondary">
                {motivoDaClasse(summary.classification)}
              </Typography>
            </Box>
          ),
      },
      {
        id: 'flammables',
        label: 'Inflamáveis',
        render: ({ summary }) => {
          if (summary.situation === 'DESATIVADA') return '—';
          if (summary.substanceCount === 0) return 'Nenhum produto';
          return (
            <Box sx={columnStackSx}>
              <span>{capacidadesEmTexto(summary)}</span>
              <Typography variant="body2" color="text.secondary">
                {produtosEmTexto(summary.substanceCount)}
              </Typography>
            </Box>
          );
        },
      },
      {
        id: 'prontuario',
        label: 'Prontuário',
        render: (installation) =>
          installation.summary.situation === 'DESATIVADA' ? '—' : <ProntuarioCell installation={installation} />,
      },
      {
        id: 'nextDue',
        label: 'Próxima revisão',
        render: ({ summary }) =>
          summary.situation === 'DESATIVADA' ? '—' : <ProximaRevisaoCell nextDueDate={summary.nextDueDate} />,
      },
      { id: 'situation', label: 'Situação', render: ({ summary }) => <SituationChip situation={summary.situation} /> },
      {
        id: 'actions',
        label: 'Ações',
        align: 'center',
        render: (installation) => {
          const canEdit = !editar.disabled;
          const abrir = canEdit ? editar : ver;
          return (
            <Box sx={{ display: 'flex', gap: 0.5, justifyContent: 'center' }}>
              <ControleIconButton
                controle={abrir}
                onClick={() => openInstallation(installation)}
                sx={{ width: 44, height: 44 }}
              >
                {canEdit ? <EditIcon /> : <ViewIcon />}
              </ControleIconButton>
              {!excluir.disabled && (
                <ControleIconButton
                  controle={excluir}
                  color="error"
                  onClick={() => handleDeleteClick(installation)}
                  sx={{ width: 44, height: 44 }}
                >
                  <DeleteIcon />
                </ControleIconButton>
              )}
            </Box>
          );
        },
      },
    ],
    [departmentName, editar, excluir, handleDeleteClick, openInstallation, ver]
  );

  const goToNewInstallation = () => navigate('/flammable-installations/new');

  return (
    <PageShell
      breadcrumbs={[{ label: 'Início', path: '/' }, { label: 'Instalações com inflamáveis' }]}
      title="Instalações com inflamáveis"
      subtitle="O prontuário de cada empresa: o que ela armazena, a classe de cada instalação e os documentos que a NR 20 exige."
      actions={
        <RouteActionButton
          para="/flammable-installations/new"
          exige={pode('flammable_installation.create')}
          variant="contained"
          color="primary"
          startIcon={<AddIcon />}
        >
          Nova instalação
        </RouteActionButton>
      }
    >
      {error && !loading && (
        <Alert
          severity="error"
          sx={{ mb: 3 }}
          action={
            <Button color="inherit" onClick={() => void loadInstallations()} sx={{ minHeight: 44 }}>
              Tentar de novo
            </Button>
          }
        >
          {error}
        </Alert>
      )}

      <Box sx={{ mb: 3 }}>
        <InlineFilterBar
          searchPlaceholder="Nome, identificação ou localização"
          searchValue={filters.search}
          onSearchChange={(value) => setFilter('search', value)}
          resultCount={total}
          resultNoun="instalações"
          chips={activeChips}
          onClearAll={clearAll}
          loading={loading}
        >
          <Tooltip title={departmentList.bloqueio ?? ''}>
            <span>
              <InlineSelect
                label="Setor"
                value={filters.departmentId}
                options={departments}
                onChange={(value) => setFilter('departmentId', value)}
                emptyLabel="Todos"
                minWidth={180}
                disabled={!departmentList.permitido}
              />
            </span>
          </Tooltip>
          <InlineSelect
            label="Classe"
            value={filters.class}
            options={CLASS_OPTIONS}
            onChange={(value) => setFilter('class', value)}
            emptyLabel="Todas"
            minWidth={180}
          />
          <InlineSelect
            label="Situação"
            value={filters.situation}
            options={SITUATION_OPTIONS}
            onChange={(value) => setFilter('situation', value)}
            emptyLabel="Todas"
            minWidth={180}
          />
        </InlineFilterBar>
      </Box>

      {!(error && installations.length === 0) && (
        <DataTable
          columns={columns}
          rows={installations}
          getRowId={(installation) => installation.id}
          loading={loading}
          filtersActive={filtersActive}
          onClearFilters={clearAll}
          emptyState={{
            title: 'Nenhuma instalação com inflamáveis cadastrada para esta empresa',
            description:
              'O Laudo NR 20 lista cada instalação, o que ela armazena e os documentos do prontuário. Comece cadastrando a primeira: diga o que ela faz e o que guarda, e o sistema calcula a classe.',
            action: {
              label: 'Cadastrar a primeira instalação',
              node: 'flammable-installations',
              deniedHint: 'Peça a um administrador para cadastrar as instalações com inflamáveis desta empresa.',
              onClick: goToNewInstallation,
              allowed: !criar.disabled,
            },
          }}
          pagination={{
            page,
            rowsPerPage,
            totalCount: total,
            onPageChange: setPage,
            onRowsPerPageChange: (value) => {
              setRowsPerPage(value);
              setPage(0);
            },
          }}
        />
      )}

      <DeleteWithDependenciesDialog
        open={Boolean(deleteTarget)}
        loading={deleteDependencies === null}
        blocked={Boolean(deleteDependencies?.hasDependencies)}
        title="Excluir instalação"
        confirmText={`Tem certeza que deseja excluir ${deleteTarget ? `"${deleteTarget.name}"` : 'esta instalação'}?`}
        prerequisite={{
          title: 'Esta instalação ainda tem itens dentro',
          reason: 'Para excluir a instalação, tire antes os produtos e os registros do prontuário.',
          items: dependencyItems(deleteDependencies?.details),
          resolve: {
            label: 'Abrir a instalação',
            permission: 'flammable_installation.update',
            mode: 'navigate',
            route: `/flammable-installations/${deleteTarget?.id}`,
          },
          deniedHint: 'Peça a um administrador para tirar os produtos e os registros desta instalação antes de excluí-la.',
        }}
        onClose={closeDeleteDialog}
        onConfirm={handleDeleteConfirm}
      />
    </PageShell>
  );
};

export default FlammableInstallationListPage;
